import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useNavigateTo from 'hooks/useNavigateTo'
import useCharterFlightPromotionAvailableSeats from 'hooks/Flights/useCharterFlightPromotionAvailableSeats'
import { getImageUrl } from 'lib/image/imageUtils'
import { CHARTER_PAGE_SLUG, MALDIVES_MARKETING_BANNER_IMAGE_ID } from 'constants/charterFlight'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineClockWithTickIcon from 'LuxKit/Icons/line/LineClockWithTickIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Label from 'LuxKit/Label/Label'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import FormatCurrency from 'components/Common/FormatCurrency'

const HeroImageContainer = styled.div`
  position: relative;
  height: ${rem(200)};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
`

const CloseButtonContainer = styled.div`
  position: absolute;
  top: ${rem(20)};
  inset-inline-end: ${rem(20)};
`

function MaldivesDirectHundredDollarSaleModal() {
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const navigateTo = useNavigateTo()
  const { availableSeats } = useCharterFlightPromotionAvailableSeats()

  const handleClose = useCallback(() => {
    modalElement?.resolve()
  }, [modalElement])

  const handleViewBundles = useCallback(() => {
    modalElement?.resolve()
    navigateTo(`/campaign/${CHARTER_PAGE_SLUG}`)
  }, [modalElement, navigateTo])

  const heroImageUrl = getImageUrl(MALDIVES_MARKETING_BANNER_IMAGE_ID, {
    width: 720,
    height: 200,
  })

  return (
    <ModalBase size="L" mode="default" dismissible>
      <ModalBody>
        <HeroImageContainer>
          <img
            src={heroImageUrl}
            alt={intl.formatMessage({
              id: '0ivxMLKH-CNOP_tA7k1R',
              defaultMessage: 'Maldives charter plane flying over islands',
              description: 'Accessibility text for the promotional Maldives charter modal hero image',
            })}
          />
          <CloseButtonContainer>
            <IconButton
              kind="secondary"
              variant="dark"
              onClick={handleClose}
              aria-label={intl.formatMessage({
                id: 'Gb5x06LhiEjM82O8-0GN',
                defaultMessage: 'Close',
                description: 'Accessible aria-label for the close button to dismiss the current overlay or modal',
              })}
            >
              <LineTimesIcon />
            </IconButton>
          </CloseButtonContainer>
        </HeroImageContainer>

        <Sheet background="normal-background" padding={24}>
          <Group direction="vertical" gap={20}>
            <Group direction="vertical" gap={12}>
              <Group direction="horizontal" gap={8} wrap="wrap">
                <Label
                  kind="primary"
                  variant="urgency"
                  startIcon={<SolidFireIcon />}
                >
                  {intl.formatMessage({
                    id: 'sW8RHHlJM1QGkzhbQFQd',
                    defaultMessage: 'Limited Time Sale',
                    description: 'Urgency label for the limited-time Maldives charter flight promotion modal',
                  })}
                </Label>
                <Label
                  kind="secondary"
                  variant="urgency"
                >
                  {intl.formatMessage(
                    {
                      id: '3Ytzb2ldekFIjZBqW00U',
                      defaultMessage: 'Hurry, {count, plural, one {# seat} other {# seats}} left at this price',
                      description: 'Inventory urgency label showing the number of remaining promotional seats',
                    },
                    { count: availableSeats },
                  )}
                </Label>
              </Group>

              <Group direction="vertical" gap={4}>
                <Heading variant="heading3">
                  <FormattedMessage
                    id="89bDjcUjoN4ht19x6SjC"
                    defaultMessage="100 seats at <em>{returnFare} return</em> to the Maldives"
                    description="Headline for the Maldives charter promotional modal describing the flight sale"
                    values={{
                      returnFare: <FormatCurrency value={100} currency="AUD" />,
                      em: (chunks) => <em>{chunks}</em>,
                    }}
                  />
                </Heading>
                <BodyText variant="medium" colour="neutral-two">
                  {intl.formatMessage({
                    id: 'Czvi_aOmB2hgJGY20LBa',
                    defaultMessage: 'Included with select hotel packages, on select dates. Economy class.',
                    description: 'Short explanation of sale eligibility in the Maldives charter promotional modal',
                  })}
                </BodyText>
              </Group>
            </Group>

            <Group direction="vertical" gap={8}>
              <BodyTextBlock
                variant="large"
                startIcon={<LinePlaneDepartureIcon />}
              >
                {intl.formatMessage({
                  id: 'nIIIm5GoE8eLLZTOrCdS',
                  defaultMessage: 'Save up to 20 hours off your travel time with our direct charter',
                  description: 'Benefit line in the Maldives charter promotion modal about direct flight time savings',
                })}
              </BodyTextBlock>
              <BodyTextBlock
                variant="large"
                startIcon={<LineCalendarIcon />}
              >
                {intl.formatMessage({
                  id: 'CQ7boATDPCeqZ3Cjnuup',
                  defaultMessage: 'Free cancellation within 7 days of purchase, and flexible date change options.',
                  description: 'Benefit line in the Maldives charter promotion modal about flexible booking terms',
                })}
              </BodyTextBlock>
              <BodyTextBlock
                variant="large"
                startIcon={<LineClockWithTickIcon />}
              >
                {intl.formatMessage({
                  id: 'ovtp4XRF7YELLAEDsjQ5',
                  defaultMessage: 'Overnight flights paired with early check-in & late checkout',
                  description: 'Benefit line in the Maldives charter promotion modal about maximising time at destination',
                })}
              </BodyTextBlock>
            </Group>
          </Group>
        </Sheet>
      </ModalBody>

      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'yV3T_GqWpzSzJGi49swv',
            defaultMessage: 'View all bundles',
            description: 'Primary button text in the Maldives charter promotional modal linking to all bundle offers',
          }),
          onClick: handleViewBundles,
        }}
      />
    </ModalBase>
  )
}

export default MaldivesDirectHundredDollarSaleModal
